import React from "react";
import { AlertTriangle, RefreshCw, ArrowLeft, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  globalError: string | null;
}

export class GDBErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { hasError: false, error: null, errorInfo: null, globalError: null };

  private onWindowError = (e: ErrorEvent) => {
    this.setState({ globalError: `${e.message} @ ${e.filename}:${e.lineno}:${e.colno}` });
  };
  private onRejection = (e: PromiseRejectionEvent) => {
    this.setState({ globalError: `Unhandled promise: ${String(e.reason?.message || e.reason)}` });
  };

  componentDidMount() {
    window.addEventListener("error", this.onWindowError);
    window.addEventListener("unhandledrejection", this.onRejection);
  }
  componentWillUnmount() {
    window.removeEventListener("error", this.onWindowError);
    window.removeEventListener("unhandledrejection", this.onRejection);
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[GDB ErrorBoundary]", error, errorInfo);
    this.setState({ errorInfo });
  }

  private reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, globalError: null });
  };

  private copy = () => {
    const { error, errorInfo, globalError } = this.state;
    const text = [
      `URL: ${window.location.href}`,
      `UA: ${navigator.userAgent}`,
      `Time: ${new Date().toISOString()}`,
      globalError ? `Global: ${globalError}` : "",
      error ? `Error: ${error.name}: ${error.message}` : "",
      error?.stack ? `Stack:\n${error.stack}` : "",
      errorInfo?.componentStack ? `Component stack:${errorInfo.componentStack}` : "",
    ].filter(Boolean).join("\n\n");
    navigator.clipboard?.writeText(text);
  };

  render() {
    const { hasError, error, errorInfo, globalError } = this.state;
    if (!hasError && !globalError) return this.props.children;

    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-2xl w-full rounded-lg border bg-card shadow-sm p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-destructive/10 text-destructive">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">No se pudo cargar el módulo GDB</h1>
              <p className="text-sm text-muted-foreground">
                Se ha capturado un error de diagnóstico. Puedes reintentar o volver al inicio.
              </p>
            </div>
          </div>

          <div className="rounded-md bg-muted/50 border p-3 text-sm font-mono space-y-2 max-h-80 overflow-auto">
            {error && (
              <div>
                <div className="font-semibold text-destructive">{error.name}: {error.message}</div>
                {error.stack && (
                  <pre className="whitespace-pre-wrap text-xs mt-1 opacity-80">{error.stack}</pre>
                )}
              </div>
            )}
            {globalError && (
              <div>
                <div className="font-semibold">Global error</div>
                <pre className="whitespace-pre-wrap text-xs opacity-80">{globalError}</pre>
              </div>
            )}
            {errorInfo?.componentStack && (
              <div>
                <div className="font-semibold mt-2">Component stack</div>
                <pre className="whitespace-pre-wrap text-xs opacity-80">{errorInfo.componentStack}</pre>
              </div>
            )}
            {!error && !globalError && (
              <div className="text-muted-foreground">Sin detalles disponibles.</div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={this.reset}>
              <RefreshCw className="h-4 w-4" /> Reintentar
            </Button>
            <Button variant="outline" onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4" /> Recargar página
            </Button>
            <Button variant="outline" onClick={this.copy}>
              <Copy className="h-4 w-4" /> Copiar diagnóstico
            </Button>
            <Button variant="ghost" onClick={() => (window.location.href = "/home")}>
              <ArrowLeft className="h-4 w-4" /> Volver al inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
